import {
  constructSortFn, createPaginatedRowModel, createSortedRowModel, functionalUpdate, metaHelper, rowPaginationFeature,
  rowSelectionFeature, rowSortingFeature, sortFn_basic, tableFeatures, useTable, type PaginationState, type Row,
  type RowData, type RowSelectionState, type SortingState, type TableOptions
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/** What a column tells the table that draws it. */
export interface DataTableColumnMeta {
  /** In pixels. The columns without one share what the others leave. */
  width?: number;
  /** Numbers and the actions stand at the end of their cell, everything else at its start. */
  align?: 'start' | 'end';
  /**
   * Stays in view while the table scrolls sideways: the column that names the row at the
   * start, its actions at the end. One of each.
   */
  sticky?: 'start' | 'end';
}

/** Text in the order of the interface's language, which the document's `lang` names: "Ç" after "C", not after "Z". */
const localeSortFn = constructSortFn({
  sort: (a, b) => String(a ?? '').localeCompare(String(b ?? ''), document.documentElement.lang),
});

/**
 * What a table of the app can do. A column picks its sort by name: `locale` for text, `basic`
 * for dates, numbers and codes.
 */
const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { locale: localeSortFn, basic: sortFn_basic },
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
  rowSelectionFeature,
  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

export const pageSizes = [15, 25, 50, 100];
/** The page size while the address names none. */
export const defaultPageSize = 15;

/**
 * The address's query, and a writer of some of its keys that keeps the rest. Replaces the
 * history entry, so that Back leaves the page rather than stepping through every key pressed
 * in a search.
 */
export const useTableParams = () => {
  const [params, setSearchParams] = useSearchParams();

  const setParams = (values: Record<string, string | null>) => {
    // From the address as it is now, not as this render read it: the router applies a change in
    // a transition, and a write made before that lands (a search whose pause ends as a segment is
    // clicked) would otherwise put back what the other one had just changed.
    setSearchParams(() => {
      const next = new URLSearchParams(window.location.search);

      for (const [key, value] of Object.entries(values)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }

      return next;
    }, { replace: true });
  };

  return { params, setParams };
};

/**
 * Where the address puts a table's rows: its sort, when that names one of `sortable`, else
 * `defaultSort`; its page, from 0; its page size, one of `pageSizes`. A table the server pages
 * asks the server for exactly this.
 */
export const tableAddress = (params: URLSearchParams, sortable: readonly string[], defaultSort: string) => {
  const sortParam = params.get('sort') ?? '';
  const sizeParam = Number(params.get('size'));

  return {
    sort: sortable.includes(sortParam.replace(/^-/, '')) ? sortParam : defaultSort,
    pageIndex: Math.max(0, (parseInt(params.get('page') ?? '', 10) || 1) - 1),
    pageSize: pageSizes.includes(sizeParam) ? sizeParam : defaultPageSize,
  };
};

interface UseDataTableOptions<TData extends RowData> {
  /** Kept stable between renders by the caller, as the rows are: a new array reads everything again. */
  columns: TableOptions<DataTableFeatures, TData>['columns'];
  data: TData[];
  getRowId: (row: TData) => string;
  /** The column the rows are sorted by while the address names none it can sort by, `-` before it for descending. */
  defaultSort: string;
  /** Rows a checkbox may pick; without it the table has no checkboxes. */
  canSelect?: (row: Row<DataTableFeatures, TData>) => boolean;
  /**
   * Rows a server sorts and cuts into pages, `data` being the one page: the columns it sorts by,
   * and once it has answered, how many rows there are in all and the page it answered, which is
   * the last one there is where the address names one past it. Without this the rows are all
   * here, and are sorted and paged here.
   */
  server?: { sortable: readonly string[]; rowCount?: number; pageIndex?: number };
}

/**
 * One table of the app, on rows the caller already has in full, sorted and cut into pages here,
 * or on the page a server sorted and cut (`server`). Where the rows are is the address's
 * (`sort`, `page`, `size`), so a link opens the list as it was. What is picked lives as long as
 * the page, and on a server's page until the reader turns the page or the order, which takes
 * its rows away.
 */
export const useDataTable = <TData extends RowData>({
  columns, data, getRowId, defaultSort, canSelect, server,
}: UseDataTableOptions<TData>) => {
  const { params, setParams } = useTableParams();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // An address written by hand, or kept from before a column was dropped, may name a column
  // that is not there or does not sort; the rows then take the default order. A server says
  // what it sorts by itself, so a column shown only once it has answered sorts from the start.
  const sortable = server?.sortable ?? columns
    .filter((column) => column.enableSorting !== false && ('accessorKey' in column || 'accessorFn' in column))
    .map((column) => column.id ?? ('accessorKey' in column ? String(column.accessorKey) : ''));
  const address = tableAddress(params, sortable, defaultSort);
  const sortBy = address.sort;

  // The state the table is handed stays the same object while its values do: a new one on
  // every render would have the table work its rows out again each time.
  const sorting = useMemo<SortingState>(
    () => [{ id: sortBy.replace(/^-/, ''), desc: sortBy.startsWith('-') }],
    [sortBy]
  );

  const { pageSize } = address;
  // A page the rows no longer reach, after a search or a delete, shows the last one there is;
  // a server answers that page itself.
  const pageIndex = server
    ? server.pageIndex ?? address.pageIndex
    : Math.min(address.pageIndex, Math.max(0, Math.ceil(data.length / pageSize) - 1));
  const pagination = useMemo<PaginationState>(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const table = useTable({
    features: dataTableFeatures,
    columns,
    data,
    getRowId,
    state: { sorting, pagination, rowSelection },
    enableSortingRemoval: false,
    enableMultiSort: false,
    enableRowSelection: canSelect ?? false,
    manualSorting: Boolean(server),
    manualPagination: Boolean(server),
    rowCount: server?.rowCount,
    // The rows change under a page that is read again after every edit; the reader stays
    // where they were, and only what they change themselves goes back to the first page.
    autoResetPageIndex: false,
    onSortingChange: (updater) => {
      const [next] = functionalUpdate(updater, sorting);
      const value = next ? `${next.desc ? '-' : ''}${next.id}` : null;

      setParams({ sort: value === defaultSort ? null : value, page: null });
      if (server) setRowSelection({});
    },
    onPaginationChange: (updater) => {
      const next = functionalUpdate(updater, pagination);

      setParams({
        page: next.pageIndex > 0 ? String(next.pageIndex + 1) : null,
        size: next.pageSize !== defaultPageSize ? String(next.pageSize) : null,
      });
      if (server) setRowSelection({});
    },
    onRowSelectionChange: setRowSelection,
  });

  return table;
};
